<template>
  <main-layout
    controlsBg="light"
    :hasControlsBar="false"
    :breadcrumb="breadcrumb">
    <div class="container">
      <grid-row class="margin-top-xl" column-align="center">
        <grid-col col-size="6">
          <div id="header">
            <h1 class="header-title">
              <chevron @click="$router.back()"/>
              {{$t('health.analytics.evaluation.title')}}
            </h1>
          </div>
        </grid-col>
      </grid-row>

      <grid-row class="margin-top-xl" column-align="center">
        <grid-col col-size="6">
          <div class="list-of-categories">
            <div class="categories">
              <div class="category-item-container" v-for="(category, index) in categories" :key="index">

                <div class="category-item" @click="toggleCategory(category.categoryId)">
                  <div class="category-info">
                    <div class="color-circle" :style="{ backgroundColor: getCategoryColor(category.color) }"></div>
                    <div class="category-name">
                      {{ category.categoryName || category.name || `${$t('health.analytics.evaluation.categoryFallback')}` + category.categoryId }}
                    </div>
                  </div>
                  <div class="category-toggle">
                    <span v-if="expandedCategories.includes(category.categoryId)">▲</span>
                    <span v-else>▼</span>
                  </div>
                </div>

                <div class="biomarkers-list" v-if="expandedCategories.includes(category.categoryId)">
                  <div v-if="!category.biomarkerDetails || category.biomarkerDetails.length === 0" class="no-biomarkers">
                    Sin datos recuperados para esta categoría
                  </div>
                  <div v-for="biomarker in category.biomarkerDetails" :key="biomarker.biomarkerId" class="biomarker-wrapper">

                    <div class="biomarker-item" @click="toggleBiomarker(biomarker.biomarkerId)">
                      <div class="color-circle-small" :style="{ backgroundColor: getCategoryColor(biomarker.statusColor) }"></div>
                      <div class="biomarker-info">
                        <span class="biomarker-name">{{ biomarker.description || 'Biomarcador ' + biomarker.biomarkerId }}</span>
                        <span class="biomarker-range">
                          {{ formatRange(biomarker.limMin, biomarker.limMax) }}
                        </span>
                        <span class="biomarker-value" v-if="biomarker.value !== null">{{ biomarker.value }}</span>
                        <span class="biomarker-value empty" v-else>Sin datos</span>
                        <span class="biomarker-unit">{{ biomarker.unit}}</span>
                      </div>
                      <div class="biomarker-toggle">
                        <span v-if="expandedBiomarkers.includes(biomarker.biomarkerId)">▲</span>
                        <span v-else>▼</span>
                      </div>
                    </div>

                    <div class="evolution-container" v-if="expandedBiomarkers.includes(biomarker.biomarkerId)">
                      <div v-if="loadingEvolution[biomarker.biomarkerId]" class="loading-text">
                        {{$t('health.analytics.evaluation.loadingHistory')}}
                      </div>
                      <div v-else-if="evolutionData[biomarker.biomarkerId] && evolutionData[biomarker.biomarkerId].length > 0">
                        <p class="evolution-title">
                          {{$t('health.analytics.evaluation.evolutionTitle')}}
                        </p>
                        <table class="evolution-table">
                          <thead>
                          <tr>
                            <th>
                              {{$t('health.analytics.list.date')}}
                            </th>
                            <th>
                              {{$t('health.analytics.evaluation.value')}}
                            </th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(record, idx) in evolutionData[biomarker.biomarkerId]" :key="idx">
                            <td>{{ formatDate(record.analysisAt) }}</td>
                            <td class="bold-value">{{ record.value }} {{ biomarker.unit }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                      <div v-else class="loading-text">
                        {{$t('health.analytics.evaluation.noHistory')}}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>
            <div v-if="categories.length === 0" class="no-data">
              {{$t('health.analytics.evaluation.empty')}}
            </div>
          </div>
        </grid-col>
      </grid-row>
    </div>
  </main-layout>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Chevron from '@/components/shared/Chevron';
import selectedPatient from '../../store/selectedPatient';

export default {
  name: 'ListOfCategories',
  components: {
    MainLayout,
    GridRow,
    GridCol,
    Chevron
  },
  data() {
    return {
      categories: [],
      expandedCategories: [],
      expandedBiomarkers: [],
      evolutionData: {},
      loadingEvolution: {},
      analyticId: null,
      targetUserId: this.$route.query.targetUserId || selectedPatient.get(),
      breadcrumb: [
        {link: '/workspace', name: 'Workspace'},
        {link: '/analytics/list', name: 'Analíticas'},
        {link: '', name: 'Resultados', disabled: true}
      ]
    };
  },
  created() {
    this.analyticId = this.$route.params.analyticId;
    this.fetchCategories();
  },
  methods: {
    toggleCategory(categoryId) {
      const index = this.expandedCategories.indexOf(categoryId);
      if (index > -1) {
        this.expandedCategories.splice(index, 1);
      } else {
        this.expandedCategories.push(categoryId);
      }
    },
    async toggleBiomarker(biomarkerId) {
      const index = this.expandedBiomarkers.indexOf(biomarkerId);
      if (index > -1) {
        this.expandedBiomarkers.splice(index, 1);
      } else {
        this.expandedBiomarkers.push(biomarkerId);
        if (!this.evolutionData[biomarkerId]) {
          await this.fetchBiomarkerEvolution(biomarkerId);
        }
      }
    },
    async fetchCategories() {
      if (!this.analyticId) {
        console.error("No se ha proporcionado un analyticId en la ruta.");
        return;
      }
      try {
        let url = `/api/web/xugarhero/analytics/evaluation/analytic/${this.analyticId}/all-categories`;
        if (this.targetUserId) {
          url += `?targetUserId=${this.targetUserId}`;
        }

        const response = await this.$http.get(url);
        this.categories = Array.isArray(response.body) ? response.body : (response.body.categories || []);
      } catch (error) {
        console.error("Error al cargar las categorías:", error);
      }
    },
    getCategoryName(categoryId) {
      const names = {
        1: 'Salud Cardiovascular',
        2: 'Salud Muscular y Ósea',
        3: 'Salud Renal',
        4: 'Salud Hepática',
        5: 'Salud Hematológica',
        6: 'Salud Inmunológica',
        7: 'Salud Metabólica y Endocrina'
      };
      return names[categoryId] || 'Categoría ' + categoryId;
    },
    async fetchBiomarkerEvolution(biomarkerId) {
      this.$set(this.loadingEvolution, biomarkerId, true);

      try {
        let url = `/api/web/xugarhero/analytics/results/history/biomarker/${biomarkerId}`;
        if (this.targetUserId) {
          url += `?targetUserId=${this.targetUserId}`;
        }

        const response = await this.$http.get(url);
        this.$set(this.evolutionData, biomarkerId, response.body || response.data);
      } catch (error) {
        console.error("Error al cargar la evolución del biomarcador:", error);
        this.$set(this.evolutionData, biomarkerId, []);
      } finally {
        this.$set(this.loadingEvolution, biomarkerId, false);
      }
    },
    getCategoryColor(colorEnum) {
      const colors = {
        'GREEN': '#28a745',
        'ORANGE': '#fd7e14',
        'RED': '#dc3545',
        'GREY': '#6c757d'
      };
      return colors[colorEnum] || '#6c757d';
    },
    formatDate(dateInput) {
      if (!dateInput) return '';

      // Caso 1: El backend lo manda como Array (ej: [2023, 1, 16, 0, 0])
      if (Array.isArray(dateInput)) {
        const year = dateInput[0];
        const month = String(dateInput[1]).padStart(2, '0');
        const day = String(dateInput[2]).padStart(2, '0');
        return `${day}/${month}/${year}`;
      }

      // Caso 2: El backend lo manda como Timestamp numérico (ej: 1673827200000)
      if (typeof dateInput === 'number') {
        const date = new Date(dateInput);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }

      // Caso 3: El backend lo manda como Texto (ej: "2023-01-16 00:00..." o "2023-01-16T...")
      const dateString = String(dateInput);
      const datePart = dateString.split(/[ T]/)[0]; // Corta en el espacio o en la 'T'

      if (datePart.includes('-')) {
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
      }

      return dateString;
    },
    formatRange(min, max) {
      if (min === null && max === null) return '';
      if (min === null) return `< ${max}`;
      if (max === null) return `> ${min}`;
      return `[ ${min} - ${max} ]`;
    }
  }
};
</script>

<style scoped lang="scss">
.list-of-categories {
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.category-item-container {
  border-bottom: 1px solid #f0f0f0;
}

.category-item-container:last-child {
  border-bottom: none;
}

.category-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.category-item:hover {
  background-color: #f9f9f9;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.category-toggle {
  color: #888;
  font-size: 0.9em;
}

.biomarkers-list {
  padding: 0 15px 15px 54px;
  background-color: #fafafa;
}

/* Nuevo contenedor para agrupar fila de biomarcador + su gráfico */
.biomarker-wrapper {
  border-bottom: 1px dashed #eee;
}

.biomarker-wrapper:last-child {
  border-bottom: none;
}

.biomarker-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
    border-radius: 4px;
    padding-left: 8px;
  }
}

.color-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
}

.color-circle-small {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.biomarker-info {
  display: flex;
  align-items: center;
  width: 100%;
  font-size: 0.95em;
  color: #555;
}

.biomarker-range {
  flex: 1;
  text-align: center;
  font-size: 0.85em;
  color: #999;
  font-family: monospace;
  letter-spacing: 0.5px;
}

.biomarker-name {
  flex: 1;
  padding-right: 15px;
  font-weight: 500;
}

.biomarker-value {
  width: 100px;
  text-align: left;
  font-weight: bold;
}

.biomarker-value.empty {
  font-weight: normal;
  font-style: italic;
  color: #999;
}

.biomarker-toggle {
  color: #aaa;
  font-size: 0.8em;
  padding-right: 10px;
}

/* Estilos de la caja de evolución */
.evolution-container {
  background-color: #ffffff;
  border-left: 3px solid #1976d2;
  margin: 0 10px 15px 10px;
  padding: 15px;
  border-radius: 0 4px 4px 0;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
}

.evolution-title {
  font-weight: 600;
  color: #333;
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.evolution-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9em;

  th, td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #f5f5f5;
  }

  th {
    color: #888;
    font-weight: 500;
  }

  .bold-value {
    font-weight: bold;
    color: #1976d2;
  }
}

.loading-text {
  font-size: 0.9em;
  color: #888;
  font-style: italic;
  padding: 10px 0;
}

.category-name {
  font-size: 1.1em;
  font-weight: 500;
  color: #333;
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #888;
}

.no-biomarkers {
  padding: 12px 0;
  color: #999;
  font-style: italic;
  font-size: 0.9em;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/analytics/ListOfCategories.vue